import { all, fork, put, select, takeLatest } from '@redux-saga/core/effects';
import { createAction, createReducer, createSelector } from '@reduxjs/toolkit';
import {
  HEARINGTYPE_TYPE,
  KLEHIERARCHY_TYPE,
  KLEMAPPING_TYPE,
} from '../../constants/api';
import {
  addKleMappingsToStore,
  getAllHearingTypesInitialize,
  getAllKleHierarchiesInitialize,
  removeKleMappingsFromStore,
  updateKleMappingsInitialize,
} from '../actions/api';
import { kleMappingIdsByHearingTypeId } from '../apiFeatures/kleMappingSlice';
import { parseApiError } from '../globalMessageSlice';
import { awaitApiCall } from '../helpers/sagaHelpers';

// ---------- ACTIONS -----------
const collapseKleHierarchies = createAction(
  'page/kleMappingAdministrationPage/arekleHierarchiesCollapsed',
);
const updateKleMappings = createAction(
  'page/kleMappingAdministrationPage/updateKleMappings',
);
const setIsUpdatingKleMappings = createAction(
  'page/kleMappingAdministrationPage/isUpdatingKleMappings',
);
const getInitialDataKlePage = createAction(
  'page/kleMappingAdministrationPage/getInitialDataKlePage',
);

// ---------- REDUCERS -----------
const kleMappingAdministrationPageReducer = createReducer(
  {
    areKleHierarchiesCollapsed: true,
    isUpdatingKleMappings: false,
  },
  (builder) => {
    builder
      .addCase(collapseKleHierarchies, (state, action) => {
        state.areKleHierarchiesCollapsed = action.payload;
      })
      .addCase(setIsUpdatingKleMappings, (state, action) => {
        state.isUpdatingKleMappings = action.payload;
      });
  },
);
// ----------- Sagas -------------
export function* kleMappingAdministrationPageSaga() {
  yield all([fork(updateKleMappingsSaga), fork(getInitialDataKlePageSaga)]);
}

function* getInitialDataKlePageSaga() {
  yield takeLatest(getInitialDataKlePage.type, function* () {
    const getAllHearingTypesInitAction = yield put(
      getAllHearingTypesInitialize(),
    );
    const getAllHearingTypesInitResult = yield awaitApiCall(
      getAllHearingTypesInitAction,
    );

    if (getAllHearingTypesInitResult.success) {
      yield put(getAllKleHierarchiesInitialize());
    }
  });
}

function* updateKleMappingsSaga() {
  yield takeLatest(updateKleMappings.type, function* (action) {
    const { hearingTypeId, kleHierarchyIds } = action.payload;
    if (!hearingTypeId || !kleHierarchyIds) {
      return;
    }

    yield put(setIsUpdatingKleMappings(true));

    const kleMappingIdsToRemove = yield select(
      kleMappingIdsByHearingTypeId(hearingTypeId),
    );

    const kleMappingDtos = kleHierarchyIds.map(
      mapKleMappingModels(hearingTypeId),
    );

    const initAction = yield put(
      updateKleMappingsInitialize({ hearingTypeId, dtos: kleMappingDtos }),
    );

    const actionResult = yield awaitApiCall(initAction);

    if (actionResult.success) {
      yield put(removeKleMappingsFromStore(kleMappingIdsToRemove));
      yield put(addKleMappingsToStore(actionResult.success.payload));
    } else {
      yield put(parseApiError(actionResult.error.payload));
    }

    yield put(setIsUpdatingKleMappings(false));
  });
}

// ----------- Selectors -----------

const kleMappingAdministrationState = (state) =>
  state.page.kleMappingAdministration;

const areKleHierarchiesCollapsedSelector = createSelector(
  kleMappingAdministrationState,
  (state) => state.areKleHierarchiesCollapsed,
);
const isUpdatingKleMappingsSelector = createSelector(
  kleMappingAdministrationState,
  (state) => state.isUpdatingKleMappings,
);

export {
  areKleHierarchiesCollapsedSelector,
  collapseKleHierarchies,
  isUpdatingKleMappingsSelector,
  updateKleMappings,
  getInitialDataKlePage,
};

export default kleMappingAdministrationPageReducer;

const mapKleMappingModels = (hearingTypeId) => (kleHierachyId) => ({
  data: {
    type: KLEMAPPING_TYPE,
    attributes: {},
    relationships: {
      [HEARINGTYPE_TYPE]: {
        data: {
          id: hearingTypeId,
          type: HEARINGTYPE_TYPE,
        },
      },
      [KLEHIERARCHY_TYPE]: {
        data: {
          id: kleHierachyId,
          type: KLEHIERARCHY_TYPE,
        },
      },
    },
  },
});
